import { Component, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { catchError, filter, lastValueFrom, map, of } from "rxjs";
import { StructureService } from "src/app/services/structure.service";
import { environment } from "src/environments/environment";
import { BreadcrumbTitleService, BreadcrumbEntityTitle } from 'src/app/services/breadcrumb-title.service';

export interface BreadcrumbItem {
  name: string;
  link: string;
}

@Component({
  selector: "breadcrumbs",
  templateUrl: "./breadcrumbs.component.html",
  styleUrls: ["./breadcrumbs.component.scss"],
})
export class BreadcrumbsComponent implements OnInit {
  /**
   * Array di rotte per la breadcrumbs.
   */
  routes: BreadcrumbItem[] = [];

  constructor(
    private router: Router,
    private _structureService: StructureService,
    private breadcrumbTitleService: BreadcrumbTitleService
  ) { }

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    // Recupera l'URL corrente al caricamento della pagina
    this.updateRoutes(this.router.url);

    this.breadcrumbTitleService.title$.subscribe(title => {
      this.applyEntityTitle(title);
    });
    
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        return this.updateRoutes(event.url);
      });
  }

  /**
   * Costruisce le rotte per la breadcrumbs.
   */
  async buildBreadcrumbs(routes: string[]): Promise<BreadcrumbItem[]> {
    const tempBreadcrumbs: BreadcrumbItem[] = [];
    let accumulatedPath = "";

    routes.forEach((route: string) => {
      accumulatedPath += `/${route}`;
      tempBreadcrumbs.push({
        name: this.capitalizeFirstLetter(route.replaceAll("-", " ")),
        link: accumulatedPath,
      });
    });

    return tempBreadcrumbs;
  }

  /**
   * Triggerato al cambio di URL, aggiorna le rotte.
   */
  async updateRoutes(url: string): Promise<void> {
    this.breadcrumbTitleService.clear();

    // Rimuove i queryParams e i fragment (es. ?param=value#section)
    const cleanUrl = url.split('?')[0].split('#')[0];

    const routes = cleanUrl.substring(1).split("/");
    const areBackOffice = cleanUrl.startsWith(`/${environment.adminPath}`);
    const homeLink = areBackOffice ? "" : `/${environment.trasparenzaPath}`;

    if (cleanUrl === `/${environment.adminPath}` || cleanUrl === `/${environment.trasparenzaPath}`) {
      this.routes = [];
      return;
    }

    let tempBreadcrumbs = await this.buildBreadcrumbs(routes);
    tempBreadcrumbs = await this.checkEditOrCreateRoutes(tempBreadcrumbs, routes);
    tempBreadcrumbs = await this.setValidBreadcrumbLinks(tempBreadcrumbs);

    if (!areBackOffice && cleanUrl !== `/${environment.trasparenzaPath}`) {
      tempBreadcrumbs.unshift({ name: "Home", link: homeLink });
    }

    this.routes = tempBreadcrumbs;

    this.applyEntityTitle(this.breadcrumbTitleService.current);
  }


  /**
   * Controlla se una breadcrumb è di tipo "edit" o "create".
   */
  isEditOrCreate(route: BreadcrumbItem): boolean {
    const routeName = route.name.toLowerCase();
    return routeName.includes('edit') || routeName.includes('create');
  }

  /**
   * Controlla se una pagina esiste usando getLayout di StructureService.
   */
  async setValidBreadcrumbLinks(breadcrumbs: BreadcrumbItem[]): Promise<BreadcrumbItem[]> {
    for (const breadcrumb of breadcrumbs) {
      if (this.isEditOrCreate(breadcrumb)) {
        // Se è "edit" o "create", la rimuoviamo
        continue;
      }

      const exists = await this.checkIfPageExistsByStructureService(breadcrumb.link.substring(1));

      if (!exists) {
        // Se non esiste, la breadcrumb non deve essere linkabile
        breadcrumb.link = ''; // Imposta il link vuoto per farla diventare testo
      }
    }
    return breadcrumbs;
  }

  /**
   * Controlla e aggiorna le rotte per "edit" e "create".
   */
  async checkEditOrCreateRoutes(
    tempBreadcrumbs: BreadcrumbItem[],
    routes: string[]
  ): Promise<BreadcrumbItem[]> {
    for (const targetRoute of ["edit", "create"]) {
      const targetIndex = routes.findIndex((route) => route === targetRoute);

      if (targetIndex !== -1) {
        const previousRoute = routes[targetIndex - 1] || "";
        const previousUrl = tempBreadcrumbs.find(
          (url) => url.name.toLowerCase() === previousRoute.toLowerCase()
        );
        let pageSlug = previousUrl?.link?.substring(1) || "";

        const id = routes[targetIndex + 1] || "";

        const formattedSlug = `${pageSlug}/${targetRoute}-${previousRoute}`;

        const existsBase = await this.checkIfPageExistsByStructureService(pageSlug);
        const existsFormatted = await this.checkIfPageExistsByStructureService(formattedSlug);

        if (!existsBase && !existsFormatted) {
          tempBreadcrumbs = tempBreadcrumbs.filter(
            (route) => route.name.toLowerCase() !== previousRoute.toLowerCase()
          );
        }
      }

      tempBreadcrumbs = tempBreadcrumbs.filter(
        (route) => route.name.toLowerCase() !== targetRoute.toLowerCase()
      );
    }

    return tempBreadcrumbs;
  }



  /**
   * Controlla se una pagina esiste usando getLayout di StructureService.
   */
  async checkIfPageExistsByStructureService(url: string | undefined): Promise<boolean> {
    if (!url) {
      return false;
    }

    try {
      const response = await lastValueFrom(
        this._structureService
          .checkIfLayoutExists(url, window.location.hostname, true)
          .pipe(
            map((response: any) => {
              return response;
            }),
            catchError((error: any) => {
              return of(false);
            })
          )
      );

      return response;
    } catch (error) {
      return false;
    }
  }


  /**
   * Helper per capitalizzare la prima lettera di una stringa.
   */
  capitalizeFirstLetter(value: string): string {
    if (!value) {
      return value;
    }
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  private applyEntityTitle(info: BreadcrumbEntityTitle | null): void {
    if (!info) {
      return;
    }

    const currentPath = this.router.url.split(/[?#]/)[0];

    // Ignora eventuali risposte arrivate dopo aver cambiato pagina.
    if (info.path !== currentPath) {
      return;
    }

    const lastSegment = currentPath
      .split('/')
      .filter(Boolean)
      .pop();

    // Il titolo deve riferirsi all'ID presente nell'URL corrente.
    if (lastSegment !== info.id) {
      return;
    }

    const lastIndex = this.routes.length - 1;

    if (lastIndex < 0) {
      return;
    }

    // Il link dell'ultima breadcrumb potrebbe essere vuoto:
    // confrontiamo il nome, non il link.
    if (this.routes[lastIndex].name !== info.id) {
      return;
    }

    this.routes = this.routes.map((route, index) =>
      index === lastIndex
        ? { ...route, name: info.title }
        : route
    );
  }
}
